import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useInstagramConnect } from './useInstagramConnect';
import { getInstagramStatus } from '@/services/instagram.service';
import { queryKeys } from '@/hooks/queries/keys';

const POLL_INTERVAL_MS = 2500;
const TIMEOUT_MS = 5 * 60 * 1000;

interface State {
    isOpening: boolean;
    isPolling: boolean;
}

export const useInstagramOAuthFlow = () => {
    const queryClient = useQueryClient();
    const connect = useInstagramConnect();
    const [state, setState] = useState<State>({ isOpening: false, isPolling: false });

    const popupRef = useRef<Window | null>(null);
    const pollTimerRef = useRef<number | null>(null);
    const timeoutTimerRef = useRef<number | null>(null);
    const messageHandlerRef = useRef<((e: MessageEvent) => void) | null>(null);

    const cleanup = () => {
        if (pollTimerRef.current !== null) {
            window.clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        if (timeoutTimerRef.current !== null) {
            window.clearTimeout(timeoutTimerRef.current);
            timeoutTimerRef.current = null;
        }
        if (messageHandlerRef.current) {
            window.removeEventListener('message', messageHandlerRef.current);
            messageHandlerRef.current = null;
        }
    };

    // 웹 콜백 페이지(/instagram/callback)가 postMessage 로 보낸 연동 결과 처리 → 서버 메시지 그대로 노출
    const finishWithMessage = (status: 'success' | 'error', message?: string) => {
        cleanup();
        if (popupRef.current && !popupRef.current.closed) {
            try {
                popupRef.current.close();
            } catch {
                /* noop */
            }
        }
        popupRef.current = null;
        setState({ isOpening: false, isPolling: false });
        queryClient.invalidateQueries({ queryKey: queryKeys.instagram.all });
        if (status === 'success') toast.success('Instagram 계정이 연결됐어요');
        else toast.error(message || '인스타그램 연결에 실패했어요.');
    };

    const stop = (reason: 'success' | 'closed' | 'timeout' | 'failed') => {
        cleanup();
        if (popupRef.current && !popupRef.current.closed) {
            try {
                popupRef.current.close();
            } catch {
                /* noop */
            }
        }
        popupRef.current = null;
        setState({ isOpening: false, isPolling: false });

        queryClient.invalidateQueries({ queryKey: queryKeys.instagram.all });

        if (reason === 'success') toast.success('Instagram 계정이 연결됐어요');
        if (reason === 'timeout') toast.error('연결 시간이 초과됐어요. 다시 시도해주세요');
        // 팝업이 닫혔는데 미연결 — 취소/거부/중복연동 등. 구체 사유(중복 시 마스킹 메시지)는 연결 창(studio)이 노출.
        if (reason === 'failed')
            toast.error('인스타그램 연결이 완료되지 않았어요. 연결 창의 안내를 확인하거나 다시 시도해 주세요.');
    };

    const start = async () => {
        if (state.isOpening || state.isPolling) return;
        setState({ isOpening: true, isPolling: false });

        try {
            const { redirectUrl } = await connect.mutateAsync();

            const popup = window.open(
                redirectUrl,
                'instagram_oauth',
                'popup,width=520,height=720,noopener=no',
            );

            if (!popup) {
                setState({ isOpening: false, isPolling: false });
                toast.error('팝업이 차단됐어요. 브라우저 설정을 확인해주세요');
                return;
            }

            popupRef.current = popup;
            setState({ isOpening: false, isPolling: true });

            // 웹 콜백 페이지가 결과를 postMessage 로 알려주면(서버가 웹 URL로 리다이렉트한 경우) 그걸 우선 사용
            const handler = (event: MessageEvent) => {
                if (event.origin !== window.location.origin) return;
                const data = event.data;
                if (!data || data.source !== 'instagram-oauth') return;
                finishWithMessage(data.status === 'success' ? 'success' : 'error', data.message);
            };
            messageHandlerRef.current = handler;
            window.addEventListener('message', handler);

            pollTimerRef.current = window.setInterval(async () => {
                if (popupRef.current?.closed) {
                    try {
                        const status = await getInstagramStatus(true);
                        queryClient.setQueryData(queryKeys.instagram.status(true), status);
                        // 팝업을 닫았는데 미연결이면 연동 실패(중복 연동 등) 가능성 → 안내
                        stop(status.isConnected ? 'success' : 'failed');
                    } catch {
                        stop('closed');
                    }
                    return;
                }

                try {
                    const status = await getInstagramStatus(true);
                    queryClient.setQueryData(queryKeys.instagram.status(true), status);
                    if (status.isConnected) stop('success');
                } catch {
                    /* 폴링 중 일시적 오류는 무시하고 다음 tick 재시도 */
                }
            }, POLL_INTERVAL_MS);

            timeoutTimerRef.current = window.setTimeout(() => stop('timeout'), TIMEOUT_MS);
        } catch (err: any) {
            setState({ isOpening: false, isPolling: false });
            const message = err?.data?.message || err?.message || 'Instagram 연결을 시작하지 못했어요';
            toast.error(typeof message === 'string' ? message : 'Instagram 연결을 시작하지 못했어요');
        }
    };

    useEffect(() => () => cleanup(), []);

    return {
        start,
        isOpening: state.isOpening,
        isPolling: state.isPolling,
        isBusy: state.isOpening || state.isPolling,
    };
};
